/*
 * Photo Headshot — crop the source photo to the detected face region.
 * Ported from the DecKorate POC's `cropToFace` (src/utils/imageClassifier.js):
 * pad the face box, square it off around its center, and draw it onto a
 * fixed-size square canvas with a circular clip so the exported PNG is
 * already a clean circle (no separate mask shape needed on the slide).
 */

import type { FaceBox } from "../../../types";

/** Output size, in pixels — matches the POC's standardized crop size. */
const OUTPUT_SIZE = 512;

/** Extra margin around the detected face box, as a fraction of its size. */
const DEFAULT_PADDING = 0.2;

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode the source photo"));
    img.src = dataUrl;
  });
}

/** Crop `imageBase64` to a padded, squared, circular region around `faceBox`. */
export async function cropToFace(
  imageBase64: string,
  mimeType: string,
  faceBox: FaceBox,
  padding: number = DEFAULT_PADDING,
): Promise<{ base64: string; mimeType: string }> {
  const img = await loadImage(`data:${mimeType};base64,${imageBase64}`);
  const imgW = img.naturalWidth;
  const imgH = img.naturalHeight;

  let fx = (faceBox.x / 100) * imgW;
  let fy = (faceBox.y / 100) * imgH;
  let fw = (faceBox.width / 100) * imgW;
  let fh = (faceBox.height / 100) * imgH;

  const padX = fw * padding;
  const padY = fh * padding;
  fx = Math.max(0, fx - padX);
  fy = Math.max(0, fy - padY);
  fw = Math.min(imgW - fx, fw + padX * 2);
  fh = Math.min(imgH - fy, fh + padY * 2);

  const size = Math.max(fw, fh);
  const centerX = fx + fw / 2;
  const centerY = fy + fh / 2;
  const cropX = Math.max(0, Math.min(imgW - size, centerX - size / 2));
  const cropY = Math.max(0, Math.min(imgH - size, centerY - size / 2));
  const cropSize = Math.min(size, imgW - cropX, imgH - cropY);

  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  const radius = OUTPUT_SIZE / 2;
  ctx.beginPath();
  ctx.arc(radius, radius, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  ctx.drawImage(img, cropX, cropY, cropSize, cropSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  const dataUrl = canvas.toDataURL("image/png");
  return { base64: dataUrl.split(",")[1] ?? "", mimeType: "image/png" };
}
